import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Music, ExternalLink, X, BarChart2, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SPOTIFY_CLIENT_ID = '5371814e58f54100b2b55084b641665f';
const SPOTIFY_SCOPES = 'user-read-recently-played user-read-playback-state user-read-private';
const REDIRECT_URI = window.location.origin + '/spotify-cb';

// ---- PKCE helpers ----
function generateRandomString(len) {
  const arr = window.crypto.getRandomValues(new Uint8Array(len));
  return Array.from(arr, b => ('0' + b.toString(36)).slice(-1)).join('').slice(0, len);
}
async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}
// ----------------------

function getSpotifyToken() { return localStorage.getItem('spotify_token'); }
function setSpotifyToken(t) { localStorage.setItem('spotify_token', t); localStorage.setItem('spotify_token_time', Date.now()); }
function clearSpotifyToken() { localStorage.removeItem('spotify_token'); localStorage.removeItem('spotify_token_time'); }
function isTokenValid() {
  const t = localStorage.getItem('spotify_token');
  const ts = Number(localStorage.getItem('spotify_token_time') || 0);
  return t && (Date.now() - ts) < 3500000; // ~58 min
}

async function spotifyFetch(path) {
  const token = getSpotifyToken();
  if (!token) throw new Error('No token');
  const res = await fetch('https://api.spotify.com/v1' + path, {
    headers: { Authorization: 'Bearer ' + token }
  });
  if (res.status === 401) { clearSpotifyToken(); throw new Error('Token expired'); }
  if (!res.ok) throw new Error('Spotify error ' + res.status);
  if (res.status === 204) return null;
  return res.json();
}

export default function SpotifyWidget({ api, isFocusActive, focusSessionId, focusDurationMin }) {
  const [connected, setConnected] = useState(isTokenValid());
  const [nowPlaying, setNowPlaying] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [recentTracks, setRecentTracks] = useState([]);
  const tracksThisSession = useRef([]);
  const pollRef = useRef(null);

  // Handle OAuth callback via hash fragment (legacy - kept for safety)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('access_token');
      if (token) { setSpotifyToken(token); setConnected(true); window.history.replaceState({}, '', window.location.pathname); }
    }
  }, []);

  const fetchNowPlaying = useCallback(async () => {
    if (!isTokenValid()) { setConnected(false); return; }
    try {
      const [current, recent] = await Promise.allSettled([
        spotifyFetch('/me/player/currently-playing'),
        spotifyFetch('/me/player/recently-played?limit=1'),
      ]);

      let track = null;
      let source = 'recent';

      if (current.status === 'fulfilled' && current.value?.item) {
        const item = current.value.item;
        track = {
          track_id: item.id,
          track_name: item.name,
          artist: item.artists?.map(a => a.name).join(', '),
          artist_id: item.artists?.[0]?.id,
          album_art: item.album?.images?.[0]?.url,
          is_playing: current.value.is_playing,
          source: 'live',
        };
        source = 'live';
      } else if (recent.status === 'fulfilled' && recent.value?.items?.length) {
        const item = recent.value.items[0];
        const t = item.track;
        track = {
          track_id: t.id,
          track_name: t.name,
          artist: t.artists?.map(a => a.name).join(', '),
          artist_id: t.artists?.[0]?.id,
          album_art: t.album?.images?.[0]?.url,
          is_playing: false,
          played_at: item.played_at,
          source: 'recent',
        };
      }

      if (track) {
        setNowPlaying(track);
        if (isFocusActive && track.track_id) {
          const exists = tracksThisSession.current.find(tr => tr.track_id === track.track_id);
          if (!exists) tracksThisSession.current = [...tracksThisSession.current, track];
        }
      }
    } catch (e) {
      if (e.message === 'Token expired') setConnected(false);
    }
  }, [isFocusActive]);

  const loadRecentTracks = useCallback(async () => {
    if (!isTokenValid()) return;
    try {
      const res = await spotifyFetch('/me/player/recently-played?limit=10');
      if (res?.items) setRecentTracks(res.items.map(i => ({
        track_id: i.track.id,
        track_name: i.track.name,
        artist: i.track.artists?.map(a => a.name).join(', '),
        album_art: i.track.album?.images?.[0]?.url,
        played_at: i.played_at,
      })));
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!isFocusActive && tracksThisSession.current.length > 0) {
      api.post('/spotify/save-session-tracks', {
        session_id: focusSessionId,
        tracks: tracksThisSession.current,
        duration_min: focusDurationMin || 0,
      }).catch(() => {});
      tracksThisSession.current = [];
    }
  }, [isFocusActive, focusSessionId, focusDurationMin, api]);

  useEffect(() => {
    if (connected) {
      fetchNowPlaying();
      pollRef.current = setInterval(fetchNowPlaying, 10000);
    }
    return () => clearInterval(pollRef.current);
  }, [connected, fetchNowPlaying]);

  const connectSpotify = async () => {
    const verifier = generateRandomString(64);
    const challenge = await generateCodeChallenge(verifier);
    localStorage.setItem('spotify_pkce_verifier', verifier);

    const params = new URLSearchParams({
      client_id: SPOTIFY_CLIENT_ID,
      response_type: 'code',
      redirect_uri: REDIRECT_URI,
      scope: SPOTIFY_SCOPES,
      code_challenge_method: 'S256',
      code_challenge: challenge,
    });
    window.open('https://accounts.spotify.com/authorize?' + params, '_blank', 'width=500,height=700');

    // Poll for token saved by callback popup
    const poll = setInterval(() => {
      if (isTokenValid()) { setConnected(true); clearInterval(poll); }
    }, 1000);
    setTimeout(() => clearInterval(poll), 120000);
  };

  const loadAnalytics = async () => {
    await loadRecentTracks();
    try {
      const res = await api.get('/spotify/analytics');
      setAnalytics({ ...res.data, recentTracks });
    } catch (e) {
      setAnalytics({ recentTracks, total_sessions: 0, top_genres: [], sessions: [] });
    }
    setShowAnalytics(true);
  };

  if (!connected) {
    return (
      <div className="card" style={{ padding: '16px', marginBottom: '12px' }}>
        <div className="flex items-center gap-3">
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1DB954', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Music size={18} color="#fff" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm">Spotify verbinden</div>
            <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Musik tracken während du fokussiert bist</div>
          </div>
          <button onClick={connectSpotify} style={{ background: '#1DB954', color: '#fff', border: 'none', borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Verbinden
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card" style={{ padding: '12px 16px', marginBottom: '12px' }}>
        <div className="flex items-center gap-3">
          {nowPlaying?.album_art ? (
            <img src={nowPlaying.album_art} alt="album" style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: 6, background: '#1DB954', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Music size={20} color="#fff" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            {nowPlaying?.track_name ? (
              <>
                <div className="font-semibold text-sm truncate">{nowPlaying.track_name}</div>
                <div className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>
                  {nowPlaying.artist}
                  {nowPlaying.source === 'recent' && <span style={{ color: '#1DB954', marginLeft: 6 }}>· zuletzt gespielt</span>}
                  {nowPlaying.is_playing && <span style={{ color: '#1DB954', marginLeft: 6 }}>♪ läuft</span>}
                </div>
              </>
            ) : (
              <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Spotify verbunden</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {nowPlaying?.track_id && (
              <a href={`https://open.spotify.com/track/${nowPlaying.track_id}`} target="_blank" rel="noreferrer" style={{ color: '#1DB954' }} title="In Spotify öffnen">
                <ExternalLink size={15} />
              </a>
            )}
            <button onClick={fetchNowPlaying} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-foreground)' }} title="Aktualisieren"><RefreshCw size={14} /></button>
            <button onClick={loadAnalytics} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-foreground)' }} title="Analyse"><BarChart2 size={16} /></button>
          </div>
        </div>
        {isFocusActive && tracksThisSession.current.length > 0 && (
          <div className="text-xs mt-2" style={{ color: '#1DB954' }}>
            {tracksThisSession.current.length} Song{tracksThisSession.current.length > 1 ? 's' : ''} getrackt
          </div>
        )}
      </div>

      <AnimatePresence>
        {showAnalytics && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setShowAnalytics(false)}>
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} onClick={e => e.stopPropagation()} style={{ background: 'var(--background)', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Musik-Analyse</h3>
                <button onClick={() => setShowAnalytics(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
              </div>

              {analytics?.top_genres?.length > 0 && (
                <div className="mb-6">
                  <div className="font-semibold text-sm mb-3" style={{ color: 'var(--muted-foreground)' }}>Genre → Focus-Dauer</div>
                  {analytics.top_genres.slice(0, 5).map((g, i) => (
                    <div key={i} className="flex items-center gap-3 mb-2">
                      <div className="text-sm" style={{ minWidth: 120, textTransform: 'capitalize' }}>{g.genre}</div>
                      <div style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#1DB954', borderRadius: 4, width: `${Math.min((g.avg_focus_min / (analytics.top_genres[0]?.avg_focus_min || 1)) * 100, 100)}%` }} />
                      </div>
                      <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{g.avg_focus_min} Min</div>
                    </div>
                  ))}
                </div>
              )}

              {recentTracks.length > 0 && (
                <div>
                  <div className="font-semibold text-sm mb-3" style={{ color: 'var(--muted-foreground)' }}>Zuletzt gehört</div>
                  {recentTracks.slice(0, 8).map((t, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                      {t.album_art && <img src={t.album_art} alt="" style={{ width: 36, height: 36, borderRadius: 4 }} />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{t.track_name}</div>
                        <div className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{t.artist}</div>
                      </div>
                      <a href={`https://open.spotify.com/track/${t.track_id}`} target="_blank" rel="noreferrer" style={{ color: '#1DB954' }}><ExternalLink size={13} /></a>
                    </div>
                  ))}
                </div>
              )}

              {!analytics?.top_genres?.length && !recentTracks.length && (
                <div className="text-center py-8" style={{ color: 'var(--muted-foreground)' }}>
                  <Music size={32} className="mx-auto mb-3 opacity-30" />
                  <p>Noch keine Daten vorhanden</p>
                  <p className="text-xs mt-1">Starte Focus während Spotify läuft!</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
